/*
 * SF Racing — product page price behaviour.
 *
 * Three jobs:
 *
 * 1. Shop Pay installments follow the selected variant.
 *    <shopify-payment-terms> sits outside the price container the theme
 *    re-renders, so its variant-id kept pointing at the variant the page
 *    loaded with.
 *
 * 2. Paid Hulk options are reflected in the price.
 *    Options like "Camber Plates (+$150.00)" are applied by the Hulk app at
 *    cart time and are not part of variant.price. Each <option> carries
 *    data-price, so we sum the selected ones and rewrite the figures.
 *
 *    Add-ons are flat for everyone — the app is not catalog-aware, so a dealer
 *    pays the same $150 a retail customer does. Cost, MAP and MSRP therefore
 *    all shift by the same amount and the savings figure stays constant.
 *
 * 3. Shop Pay installments include those add-ons.
 *    The component derives its financing figure from full_price in its
 *    shopify-meta attribute, and re-renders when that attribute changes
 *    (verified: $1,095 -> "from $98.83/mo", $1,345 -> "from $121.40/mo",
 *    exactly proportional). We rewrite full_price and price_per_term for the
 *    selected variant so the quote matches what the shopper will actually be
 *    charged — the add-ons are in the cart total at checkout, so quoting the
 *    bare variant price understated it.
 *
 *    The untouched original meta is cached on each element, so repeated
 *    updates recompute from the true baseline rather than compounding.
 *
 * Base values come from data-base-* on .sfp (cents, rendered by Liquid), so a
 * variant change resets the baseline correctly.
 *
 * Scheduling uses setTimeout, not requestAnimationFrame — rAF is throttled in
 * background tabs, which would leave figures stale on a tab the shopper
 * switched away from and back to.
 */
(function () {
  'use strict';

  var timer = null;

  function currency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  }

  function money(cents) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency() }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* Rewrite the number inside a Shopify money string, preserving its markup
     and currency symbol: '<span class=money>$1,095.00</span>' -> same with a
     new figure. Safer than rebuilding the string ourselves. */
  function reprice(sample, cents) {
    var num = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return String(sample).replace(/[\d,]+\.\d{2}/, num);
  }

  /* Sum every selected paid option, in cents. */
  function addOnCents() {
    var total = 0;

    var selects = document.querySelectorAll('.hulkapps_option select');
    for (var i = 0; i < selects.length; i++) {
      var opt = selects[i].options[selects[i].selectedIndex];
      if (opt && opt.dataset && opt.dataset.price) {
        total += parseFloat(opt.dataset.price) || 0;
      }
    }

    var checked = document.querySelectorAll('.hulkapps_option input:checked');
    for (var j = 0; j < checked.length; j++) {
      var el = checked[j];
      var p = el.dataset && el.dataset.price;
      if (!p) {
        var label = el.closest('label') || el.parentElement;
        p = label && label.dataset ? label.dataset.price : null;
      }
      if (p) total += parseFloat(p) || 0;
    }

    return Math.round(total * 100);
  }

  /* Only write when the text actually differs — otherwise our own writes
     trigger the MutationObserver below and we loop forever. */
  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function apply() {
    timer = null;

    var addon = addOnCents();
    var blocks = document.querySelectorAll('.sfp');
    var basePrice = 0;
    var variantId = null;

    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i];
      var base = parseInt(b.getAttribute('data-base-price'), 10) || 0;
      var msrp = parseInt(b.getAttribute('data-base-msrp'), 10) || 0;
      var map = parseInt(b.getAttribute('data-base-map'), 10) || 0;

      if (!basePrice) basePrice = base;
      if (!variantId) variantId = b.getAttribute('data-variant-id');

      setText(b.querySelector('[data-sfp="price"]'), money(base + addon));
      if (msrp > 0) setText(b.querySelector('[data-sfp="msrp"]'), money(msrp + addon));
      if (map > 0) setText(b.querySelector('[data-sfp="map"]'), money(map + addon));
      if (msrp > base) setText(b.querySelector('[data-sfp="save"]'), money(msrp - base));

      if (b.getAttribute('data-addon') !== String(addon)) {
        b.setAttribute('data-addon', addon);
      }
    }

    syncInstallments(variantId, basePrice + addon);
  }

  function syncInstallments(variantId, totalCents) {
    if (!variantId) return;

    var terms = document.querySelectorAll('shopify-payment-terms');

    for (var i = 0; i < terms.length; i++) {
      var t = terms[i];

      if (t.getAttribute('variant-id') !== variantId) {
        t.setAttribute('variant-id', variantId);
      }

      if (!t.sfOriginalMeta) {
        t.sfOriginalMeta = t.getAttribute('shopify-meta');
      }
      if (!t.sfOriginalMeta || !totalCents) continue;

      var meta;
      try {
        meta = JSON.parse(t.sfOriginalMeta);
      } catch (e) {
        continue;
      }
      if (!meta || !meta.variants) continue;

      for (var v = 0; v < meta.variants.length; v++) {
        if (String(meta.variants[v].id) !== String(variantId)) continue;

        var entry = meta.variants[v];
        entry.full_price = reprice(entry.full_price, totalCents);

        var count = entry.number_of_payment_terms;
        if (count > 0) {
          entry.price_per_term = reprice(entry.price_per_term, Math.round(totalCents / count));
        }
        break;
      }

      var next = JSON.stringify(meta);
      if (t.getAttribute('shopify-meta') !== next) {
        t.setAttribute('shopify-meta', next);
      }
    }
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(apply, 60);
  }

  function start() {
    var scope = document.querySelector('product-info') || document.body;

    try {
      new MutationObserver(schedule).observe(scope, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-variant-id']
      });
    } catch (e) {
      /* observer unsupported — the listeners below still cover it */
    }

    /* Hulk injects its options asynchronously and re-renders on selection,
       so listen broadly rather than binding to the controls themselves. */
    document.addEventListener('change', schedule);
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.hulkapps_option')) schedule();
    });

    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
